import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Zap, Play, Trash2, Settings2, AlertCircle, Loader2,
  ArrowRight, Calendar, Tag, User, CheckSquare, MessageSquare, Archive, Bell, UserPlus } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle } from
"@/shared/components/ui/dialog";
import { toast } from 'sonner';
import { automationApi, boardApi } from '../services/eztaskApi';

const API_URL = API_BASE;

// Icon mapping for dynamically loaded icons
const ICON_MAP = {
  ArrowRight, Plus, Calendar, Tag, User, CheckSquare, MessageSquare, Archive, Bell, UserPlus
};

export default function AutomationBuilder() {const { t } = useTranslation("eztask");
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggers, setTriggers] = useState([]);
  const [actions, setActions] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAutomation, setNewAutomation] = useState({
    name: '',
    trigger: '',
    trigger_config: {},
    action: '',
    action_config: {},
    enabled: true
  });

  // Fetch automation triggers and actions from API
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [triggersRes, actionsRes] = await Promise.all([
        fetch(`${API_URL}/api/reference/automation-triggers`),
        fetch(`${API_URL}/api/reference/automation-actions`)]
        );

        if (triggersRes.ok) {
          const triggersData = await triggersRes.json();
          setTriggers(triggersData.map((t) => ({
            value: t.code.toLowerCase(),
            label: t.label,
            icon: ICON_MAP[t.icon] || Zap
          })));
        }

        if (actionsRes.ok) {
          const actionsData = await actionsRes.json();
          setActions(actionsData.map((a) => ({
            value: a.code.toLowerCase(),
            label: a.label,
            icon: ICON_MAP[a.icon] || Zap
          })));
        }
      } catch (error) {
        console.error('Failed to fetch automation types:', error);
        // Fallback to defaults
        setTriggers([
        { value: 'card_created', label: 'When a card is created', icon: Plus },
        { value: 'card_moved', label: 'When a card is moved', icon: ArrowRight }]
        );
        setActions([
        { value: 'move_card', label: 'Move card to list', icon: ArrowRight },
        { value: 'add_label', label: 'Add a label', icon: Tag }]
        );
      }
    };
    fetchReferenceData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [automationsData, boardData] = await Promise.all([
        automationApi.list(boardId),
        boardApi.get(boardId)]
        );
        setAutomations(automationsData);
        setBoard(boardData);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        toast.error('Failed to load automations');
      } finally {
        setLoading(false);
      }
    };

    if (boardId) {
      fetchData();
    }
  }, [boardId]);

  const handleCreateAutomation = async () => {
    if (!newAutomation.name || !newAutomation.trigger || !newAutomation.action) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await automationApi.create(boardId, newAutomation);
      const updated = await automationApi.list(boardId);
      setAutomations(updated);
      setShowCreateDialog(false);
      setNewAutomation({
        name: '',
        trigger: '',
        trigger_config: {},
        action: '',
        action_config: {},
        enabled: true
      });
      toast.success('Automation created successfully');
    } catch (err) {
      toast.error('Failed to create automation');
    }
  };

  const handleTestAutomation = async (automationId) => {
    try {
      const result = await automationApi.test(automationId, null);
      toast.success('Automation test completed');
    } catch (err) {
      toast.error('Automation test failed');
    }
  };

  const getTriggerLabel = (trigger) => {
    return triggers.find((t) => t.value === trigger)?.label || trigger;
  };

  const getActionLabel = (action) => {
    return actions.find((a) => a.value === action)?.label || action;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>);

  }

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="automation-builder">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/eztask/board/${boardId}`)}
              className="text-slate-300 hover:text-white hover:bg-slate-700">
              
              <ArrowLeft className="w-4 h-4 mr-2" />{t("backToBoard", "Back to Board")}

            </Button>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              <h1 className="text-xl font-bold">{t("automationRules", "Automation Rules")}</h1>
            </div>
            {board &&
            <Badge variant="outline" className="border-slate-600 text-slate-300">
                {board.name}
              </Badge>
            }
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-automation-btn">
            
            <Plus className="w-4 h-4 mr-2" />{t("newAutomation", "New Automation")}

          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-white">{automations.length}</div>
              <div className="text-sm text-slate-400">{t("totalRules", "Total Rules")}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-400">
                {automations.filter((a) => a.enabled).length}
              </div>
              <div className="text-sm text-slate-400">{t("active", "Active")}</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-400">
                {automations.reduce((sum, a) => sum + (a.runs_count || 0), 0)}
              </div>
              <div className="text-sm text-slate-400">{t("totalRuns", "Total Runs")}</div>
            </CardContent>
          </Card>
        </div>

        {/* Automations List */}
        {automations.length === 0 ?
        <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-12 text-center">
              <Zap className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">{t("noAutomationsYet", "No automations yet")}</h3>
              <p className="text-slate-400 mb-4">{t("createAutomationRules", "Create automation rules to automate repetitive tasks on this board")}

            </p>
              <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-blue-600 hover:bg-blue-700">
              
                <Plus className="w-4 h-4 mr-2" />{t("createFirstAutomation", "Create First Automation")}

            </Button>
            </CardContent>
          </Card> :

        <div className="space-y-4">
            {automations.map((automation) =>
          <Card
            key={automation.automation_id}
            className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors"
            data-testid={`automation-${automation.automation_id}`}>
            
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white">{automation.name}</h3>
                        <Badge
                      className={automation.enabled ?
                      'bg-emerald-900/50 text-emerald-400 border-emerald-700' :
                      'bg-slate-700 text-slate-400'
                      }>
                      
                          {automation.enabled ? 'Active' : 'Disabled'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <span className="bg-slate-700 px-2 py-1 rounded">
                          {getTriggerLabel(automation.trigger)}
                        </span>
                        <span>→</span>
                        <span className="bg-slate-700 px-2 py-1 rounded">
                          {getActionLabel(automation.action)}
                        </span>
                      </div>
                      {automation.runs_count > 0 &&
                  <p className="text-xs text-slate-500 mt-2">{t("ran", "Ran")}
                    {automation.runs_count}{t("time", "time")}{automation.runs_count !== 1 ? 's' : ''}
                          {automation.last_run_at && ` • Last run: ${new Date(automation.last_run_at).toLocaleDateString()}`}
                        </p>
                  }
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-slate-400 hover:text-white"
                    onClick={() => handleTestAutomation(automation.automation_id)}
                    data-testid={`test-automation-${automation.automation_id}`}>
                    
                        <Play className="w-4 h-4" />
                      </Button>
                      <Switch
                    checked={automation.enabled}
                    onCheckedChange={() => {}}
                    data-testid={`toggle-automation-${automation.automation_id}`} />
                  
                    </div>
                  </div>
                </CardContent>
              </Card>
          )}
          </div>
        }
      </div>

      {/* Create Automation Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />{t("createAutomationRule", "Create Automation Rule")}

            </DialogTitle>
            <DialogDescription className="text-slate-400">{t("setUpA", "Set up a trigger and action to automate tasks on this board")}

            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">{t("ruleName", "Rule Name")}

              </label>
              <Input
                value={newAutomation.name}
                onChange={(e) => setNewAutomation((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Move completed tasks to Done"
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="automation-name-input" />
              
            </div>

            {/* Trigger */}
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">{t("whenThisHappens", "When this happens (Trigger)")}

              </label>
              <Select
                value={newAutomation.trigger}
                onValueChange={(value) => setNewAutomation((prev) => ({ ...prev, trigger: value }))}>
                
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="trigger-select">
                  <SelectValue placeholder="Select a trigger..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {triggers.map((trigger) => {
                    const TriggerIcon = trigger.icon;
                    return (
                      <SelectItem
                        key={trigger.value}
                        value={trigger.value}
                        className="text-white hover:bg-slate-700">
                        
                        <span className="flex items-center gap-2">
                          <TriggerIcon className="h-4 w-4" />
                          {trigger.label}
                        </span>
                      </SelectItem>);

                  })}
                </SelectContent>
              </Select>

              {/* Trigger Config - Show list selector for card_moved */}
              {newAutomation.trigger === 'card_moved' && board?.lists &&
              <div className="mt-2">
                  <Select
                  value={newAutomation.trigger_config.list_id || ''}
                  onValueChange={(value) => setNewAutomation((prev) => ({
                    ...prev,
                    trigger_config: { ...prev.trigger_config, list_id: value }
                  }))}>
                  
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder="Select target list..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {board.lists.map((list) =>
                    <SelectItem key={list.list_id} value={list.list_id.toString()} className="text-white">
                          {list.name}
                        </SelectItem>
                    )}
                    </SelectContent>
                  </Select>
                </div>
              }
            </div>

            {/* Action */}
            <div>
              <label className="text-sm font-medium text-slate-300 mb-2 block">{t("doThisAction", "Do this (Action)")}

              </label>
              <Select
                value={newAutomation.action}
                onValueChange={(value) => setNewAutomation((prev) => ({ ...prev, action: value }))}>
                
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white" data-testid="action-select">
                  <SelectValue placeholder="Select an action..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {actions.map((action) => {
                    const ActionIcon = action.icon;
                    return (
                      <SelectItem
                        key={action.value}
                        value={action.value}
                        className="text-white hover:bg-slate-700">
                        
                        <span className="flex items-center gap-2">
                          <ActionIcon className="h-4 w-4" />
                          {action.label}
                        </span>
                      </SelectItem>);

                  })}
                </SelectContent>
              </Select>

              {/* Action Config - Show list selector for move_card */}
              {newAutomation.action === 'move_card' && board?.lists &&
              <div className="mt-2">
                  <Select
                  value={newAutomation.action_config.list_id || ''}
                  onValueChange={(value) => setNewAutomation((prev) => ({
                    ...prev,
                    action_config: { ...prev.action_config, list_id: value }
                  }))}>
                  
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder="Move to list..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {board.lists.map((list) =>
                    <SelectItem key={list.list_id} value={list.list_id.toString()} className="text-white">
                          {list.name}
                        </SelectItem>
                    )}
                    </SelectContent>
                  </Select>
                </div>
              }

              {/* Action Config - Show comment input for add_comment */}
              {newAutomation.action === 'add_comment' &&
              <div className="mt-2">
                  <Input
                  value={newAutomation.action_config.comment || ''}
                  onChange={(e) => setNewAutomation((prev) => ({
                    ...prev,
                    action_config: { ...prev.action_config, comment: e.target.value }
                  }))}
                  placeholder="Comment text..."
                  className="bg-slate-900 border-slate-600 text-white" />
                
                </div>
              }
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowCreateDialog(false)}
              className="text-slate-300 hover:text-white">{t("cancel", "Cancel")}


            </Button>
            <Button
              onClick={handleCreateAutomation}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-automation-btn">{t("createAutomation", "Create Automation")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

}